import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';
import { Pageable } from '@b3networks/api/common';
import { SubscriptionLicense } from '@b3networks/api/license';
import { CustomHeaderUtil, X, X_B3_HEADER } from '@b3networks/shared/common';
import { PaginationResponse } from '@datorama/akita';
import { from, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, reduce, tap } from 'rxjs/operators';
import { BulkQuery } from '../bulk/bulk.query';
import { PurchaseSubscriptionResponse } from '../subscribed-product/subscribed-product.model';

import {
  ChangeVariantRequest,
  EditItem4,
  ExportSubscriptionReq,
  ExportSubscriptionRequestParams,
  ExportSubscriptionResp,
  ExtendRequest,
  ExtendResultItem,
  FindSubscriptionReq,
  GetAlignPriceReq,
  PurchaseRequest4,
  RecoveryResponseV2,
  SellerConfig,
  SubAlert,
  SubAlertEntry,
  Subscription,
  SubscriptionAmount,
  SubscriptionItem,
  SubscriptionNoti,
  SubscriptionNotiResponse,
  SubscriptionSkuResp,
  SubsctiptionRequestParams,
  TransactionLog
} from './subscription.model';
import { SubscriptionStore } from './subscription.store';

export const DID_RESERVATION_PRODUCTID = new InjectionToken<string>('DID_RESERVATION_PRODUCTID');
export const ORG_UUID = new InjectionToken<string>('ORG_UUID');

@Injectable({
  providedIn: 'root'
})
export class SubscriptionService {
  constructor(
    private http: HttpClient,
    private subscriptionStore: SubscriptionStore,
    @Optional() @Inject(DID_RESERVATION_PRODUCTID) private didReservationProductId: string,
    @Optional() @Inject(ORG_UUID) private b3OrgUuid: string,
    private bulkQuery: BulkQuery
  ) {}

  /**
   * @param req
   * @param pageable start from 1
   */

  findSubscriptions(
    req: FindSubscriptionReq,
    pageable?: Pageable,
    addon?: { usingPaginationPlugin: boolean },
    orgUuid?: string
  ): Observable<PaginationResponse<Subscription>> {
    req = Object.assign(new FindSubscriptionReq(), req) || new FindSubscriptionReq();
    let params = req.toParams();
    if (!pageable) {
      pageable = { perPage: 100, page: 1 };
    }

    if (orgUuid) {
      CustomHeaderUtil.setHeader('x-user-org-uuid', orgUuid);
    }
    params = params.set('page', String(pageable.page)).set('perPage', String(pageable.perPage));

    return this.http
      .get<Subscription[]>(`subscription/private/v4/subscriptions`, {
        headers: CustomHeaderUtil.onBelfOfCustomerHeader,
        params: params,
        observe: 'response'
      })
      .pipe(
        map(resp => {
          const totalCount = +resp.headers.get(X_B3_HEADER.totalCount);
          return <PaginationResponse<Subscription>>{
            currentPage: pageable.page,
            perPage: pageable.perPage,
            lastPage: Math.ceil(totalCount / pageable.perPage),
            data: resp.body.map(sub => new Subscription(sub)),
            total: totalCount
          };
        }),
        tap(page => {
          if (!addon || !addon.usingPaginationPlugin) {
            this.subscriptionStore.set(page.data);
          }
        }),
        tap(_ => this.subscriptionStore.update({ status: req.statuses }))
      );
  }

  getSubscriptionRef(
    req: FindSubscriptionReq,
    pageable: Pageable,
    orgUuid: string
  ): Observable<PaginationResponse<Subscription>> {
    let headers = new HttpHeaders();
    if (orgUuid) {
      headers = headers.set(X_B3_HEADER.orgUuid, orgUuid);
    }
    req = Object.assign(new FindSubscriptionReq(), req) || new FindSubscriptionReq();

    return this.http
      .get<Subscription[]>(`/subscription/private/v4/subscriptions`, {
        params: req.toParams(),
        headers: headers,
        observe: 'response'
      })
      .pipe(
        map(resp => {
          const totalCount = +resp.headers.get(X_B3_HEADER.totalCount);
          return <PaginationResponse<Subscription>>{
            currentPage: pageable.page,
            perPage: pageable.perPage,
            data: resp.body.map(sub => new Subscription(sub)),
            total: totalCount
          };
        })
      );
  }

  getOne(subscriptionUuid: string): Observable<Subscription> {
    return this.http
      .get<Subscription>(`subscription/private/v4/subscriptions/${subscriptionUuid}`)
      .pipe(map(sub => new Subscription(sub)));
  }

  updateSubscriptionInfo(subscriptionUuid: string, params: SubsctiptionRequestParams): Observable<void> {
    return this.http.put<void>(`/subscription/private/v5/subscriptions/${subscriptionUuid}`, params);
  }

  assignMembers(subscription: Subscription, assignees: string[]): Observable<string[]> {
    return this.http.post<string[]>(`/subscription/private/v3/subscriptions/${subscription.uuid}/assignees`, {
      assignees: assignees
    });
  }

  removeMember(subscriptionUuid: string, memberUuid: string): Observable<void> {
    return this.http.delete<void>(`/subscription/private/v3/subscriptions/${subscriptionUuid}/assignees/${memberUuid}`);
  }

  getRecoveryV2(recoveryUuid: string): Observable<RecoveryResponseV2[]> {
    return this.http.get<RecoveryResponseV2[]>(`/subscription/private/v4/subscriptions/recover/${recoveryUuid}`);
  }

  subscriptionRecoverV2(subscriptionIds: string[], recoveryUuid: string, ignoredSubscriptionUuids: string[]) {
    return this.http.post(`/subscription/private/v4/subscriptions/recover/${recoveryUuid}`, {
      subscriptionUuids: subscriptionIds,
      ignoredSubscriptionUuids: ignoredSubscriptionUuids
    });
  }

  exportSubscription(domain: string, request: ExportSubscriptionReq): Observable<void> {
    const requestParam = new HttpParams()
      .append('emails', request?.emails?.join(','))
      .append('domain', domain)
      .append('statuses', request?.statuses)
      .append('productIds', request?.productIds?.join(','));
    return this.http.get<void>(`/subscription/private/v3/subscriptions/export`, {
      params: requestParam
    });
  }

  setActive(id: string) {
    this.subscriptionStore.setActive(id);
  }

  updateActive(subscription: Subscription) {
    this.subscriptionStore.updateActive(subscription);
  }

  updateAutoRenewActive(autoRenew: boolean) {
    this.subscriptionStore.updateActive({ autoRenew: autoRenew });
  }

  buySubscribe(body: SubscriptionLicense, headers?: HttpHeaders) {
    return this.http.post<SubscriptionLicense>(`subscription/private/v1/licenses`, body, {
      headers: headers
    });
  }

  canTrial(productId: string, sku: string, buyerUuid?: string): Observable<any> {
    let headers = new HttpHeaders();
    if (buyerUuid) {
      headers = headers.set(X_B3_HEADER.orgUuid, buyerUuid);
    }
    const url = `/subscription/private/v3/subscribe/trial?productId=${productId}&sku=${sku}`;
    return this.http.get<any>(url, {
      headers: headers
    });
  }

  purchaseUnderContract(request: PurchaseRequest4, headers?: HttpHeaders): Observable<PurchaseSubscriptionResponse> {
    const url = `/subscription/private/v4/subscriptions/contract`;
    return this.http.post<PurchaseSubscriptionResponse>(url, request, {
      headers: headers
    });
  }

  getSellerConfig(buyerUuid?: string): Observable<SellerConfig> {
    let headers = new HttpHeaders();
    if (buyerUuid) {
      headers = headers.set(X_B3_HEADER.orgUuid, buyerUuid);
    }
    return this.http.get<SellerConfig>(`/subscription/private/v4/seller-config`, {
      headers: headers
    });
  }

  transfer(subscriptionUuid: string, newOrgUuid: string, orgId: string): Observable<void> {
    let headers = new HttpHeaders();
    if (orgId) {
      headers = headers.set(X_B3_HEADER.orgUuid, orgId);
    }

    const url = `/subscription/private/v3/subscriptions/${subscriptionUuid}/transfer`;
    const body = {
      newOrgUuid: newOrgUuid
    };

    return this.http.post<void>(url, body, { headers: headers });
  }

  editItem(subscriptionUuid: string, request: EditItem4, orgUuid: string): Observable<void> {
    const url = `/subscription/private/v5/subscriptions/${subscriptionUuid}/addons`;
    let headers = new HttpHeaders();
    if (orgUuid) {
      headers = headers.set(X_B3_HEADER.orgUuid, orgUuid);
    }

    return this.http.post<void>(url, request, { headers: headers });
  }

  getPrice(
    subUuid: string,
    orgUuid: string,
    invoiceNumber: string,
    startDate?: string,
    endDate?: string
  ): Observable<SubscriptionItem> {
    const url = `/subscription/private/v4/subscriptions/${subUuid}/price`;

    let params = new HttpParams().set('invoiceNumber', invoiceNumber ? invoiceNumber : '');
    if (startDate) params = params.set('startDate', startDate);
    if (endDate) params = params.set('endDate', endDate);

    let headers = new HttpHeaders();
    if (orgUuid) {
      headers = headers.set(X_B3_HEADER.orgUuid, orgUuid);
    }

    return this.http.get<SubscriptionItem>(url, {
      params: params,
      headers: headers
    });
  }

  extend(req: ExtendRequest): Observable<void> {
    const url = `/subscription/private/v5/subscriptions/${req?.subscriptionUuid}/extend`;
    const body = {
      expiryDate: req?.newExpiryDate,
      externalInvoiceNumber: req?.invoiceNumber
    };
    let headers = new HttpHeaders();
    if (req?.orgUuid) {
      headers = headers.set(X_B3_HEADER.orgUuid, req?.orgUuid);
    }

    return this.http.post<void>(url, body, { headers: headers });
  }

  extendAll(
    subscriptionUuids: string[],
    req: ExtendRequest,
    concurrency = 5,
    progressCb?: (success: number, fail: number, currentIndex: number, uuid: string) => void
  ): Observable<ExtendResultItem> {
    return from(subscriptionUuids).pipe(
      mergeMap((uuid, index) => {
        const body: ExtendRequest = {
          subscriptionUuid: uuid,
          invoiceNumber: req?.invoiceNumber,
          newExpiryDate: req?.newExpiryDate,
          orgUuid: req?.orgUuid
        };

        return this.extend(body).pipe(
          map(() => ({ success: true, uuid, index })),
          catchError(() => of({ success: false, uuid, index }))
        );
      }, concurrency),
      tap(res => {
        progressCb?.(res.success ? 1 : 0, res.success ? 0 : 1, res.index + 1, res.uuid);
      }),
      reduce(
        (acc, res) => {
          if (res.success) {
            acc.success++;
            acc.successUuids.push(res.uuid);
          } else {
            acc.fail++;
            acc.failedUuids.push(res.uuid);
          }
          return acc;
        },
        {
          success: 0,
          fail: 0,
          successUuids: [],
          failedUuids: []
        } as ExtendResultItem
      )
    );
  }

  reactivate(subscriptionUuid: string, orgUuid: string): Observable<void> {
    const url = `/subscription/private/v5/subscriptions/${subscriptionUuid}/reactivate`;

    let headers = new HttpHeaders();
    if (orgUuid) {
      headers = headers.set(X_B3_HEADER.orgUuid, orgUuid);
    }

    return this.http.post<void>(url, {}, { headers: headers });
  }

  changeVariant(subscriptionUuid: string, request: ChangeVariantRequest, orgUuid: string) {
    const url = `/subscription/private/v5/subscriptions/${subscriptionUuid}/primary-item`;
    let headers = new HttpHeaders();
    if (orgUuid) {
      headers = headers.set(X_B3_HEADER.orgUuid, orgUuid);
    }
    return this.http.post(url, request, { headers: headers });
  }

  convert(subscriptionUuid: string, invoiceNumber: string, orgUuid: string) {
    const url = `/subscription/private/v3/subscriptions/${subscriptionUuid}/convert`;
    const body = { invoiceNumber };
    let headers = new HttpHeaders();

    if (orgUuid) {
      headers = headers.set(X_B3_HEADER.orgUuid, orgUuid);
    }
    return this.http.put(url, body, { headers: headers });
  }

  getTransactionHistory(
    orgUuid: string,
    subscriptionUuid: string,
    page: number,
    perPage: number
  ): Observable<TransactionLog[]> {
    const url = `/subscription/private/v3/subscriptions/${subscriptionUuid}/history`;
    const params = {
      page: page,
      perPage: perPage
    };

    let headers = new HttpHeaders();

    if (orgUuid) {
      headers = headers.set(X_B3_HEADER.orgUuid, orgUuid);
    }

    return this.http
      .get<TransactionLog[]>(url, {
        params: params,
        headers: headers
      })
      .pipe(map(res => res.map(item => new TransactionLog(item))));
  }

  getRefundInfo(subscriptionUuid: string, orgUuid: string) {
    const url = `/subscription/private/v4/subscriptions/${subscriptionUuid}/refund`;
    let headers = new HttpHeaders();

    if (orgUuid) {
      headers = headers.set(X_B3_HEADER.orgUuid, orgUuid);
    }
    return this.http.get<any>(url, {
      headers: headers
    });
  }

  searchCreditNote(orgUuid: string) {
    const url = `/invoice/private/v2/superuser/credit_notes/search`;
    const params = {
      orgUuid: orgUuid,
      paid: true,
      awaitingPayment: false
    };
    let headers = new HttpHeaders();

    if (orgUuid) {
      headers = headers.set(X_B3_HEADER.orgUuid, orgUuid);
    }

    return this.http.get<any>(url, {
      params: params,
      headers: headers
    });
  }

  terminate(subscriptionUuid: string, creditNoteId: string, orgUuid: string, reason: string) {
    const url = `/subscription/private/v5/subscriptions/${subscriptionUuid}/terminate`;
    let headers = new HttpHeaders();
    const params = { creditNoteId: creditNoteId, reason: reason };

    if (orgUuid) {
      headers = headers.set(X_B3_HEADER.orgUuid, orgUuid);
    }

    return this.http.post(url, null, { headers: headers, params: params });
  }

  suspend(subscriptionUuid: string, orgUuid: string) {
    const url = `/subscription/private/v5/subscriptions/${subscriptionUuid}/suspend`;
    let headers = new HttpHeaders();

    if (orgUuid) {
      headers = headers.set(X_B3_HEADER.orgUuid, orgUuid);
    }

    return this.http.post(url, {}, { headers: headers });
  }

  getSkuCount(orgUuid: string, statuses = 'ACTIVE'): Observable<SubscriptionSkuResp[]> {
    let params = new HttpParams().set('statuses', statuses);
    if (orgUuid) {
      params = params.set('orgUuid', orgUuid);
    }

    return this.http
      .get<SubscriptionSkuResp[]>(`/subscription/private/v1/skuCount`, {
        headers: new HttpHeaders().set(X_B3_HEADER.orgUuid, X.orgUuid),
        params: params
      })
      .pipe(tap(value => this.subscriptionStore.update({ status: statuses })));
  }

  export(orgUuid: string, queryParams?: ExportSubscriptionRequestParams) {
    let params = new HttpParams();
    if (queryParams.skus) {
      params = params.set('skus', queryParams.skus);
    }
    if (queryParams.statuses) {
      params = params.set('statuses', queryParams.statuses);
    }

    return this.http.post<ExportSubscriptionResp>(
      `subscription/private/v1/subscriptions/export`,
      {},
      {
        params: params,
        headers: new HttpHeaders().set(X_B3_HEADER.orgUuid, orgUuid)
      }
    );
  }

  exportChucks(id: string, orgUuid: string) {
    return this.http.get<ExportSubscriptionResp>(`subscription/private/v1/subscriptions/export/${id}`, {
      headers: new HttpHeaders().set(X_B3_HEADER.orgUuid, orgUuid)
    });
  }

  getDIDReservationSubscriptions(orgUuid: string): Observable<any[]> {
    const params = {
      productIds: this.didReservationProductId
    };

    const isExp = X.orgUuid === this.b3OrgUuid;
    let headers = new HttpHeaders();
    if (!isExp && orgUuid) {
      headers = headers.set(X_B3_HEADER.orgUuid, orgUuid);
    }

    return this.http.get<any[]>(`/subscription/private/v4/subscriptions`, { params, headers });
  }

  getFailedRenewal(domain: string, startDate: string, endDate: string) {
    const params = new HttpParams().append('domain', domain).append('startDate', startDate).append('endDate', endDate);
    return this.http.get<SubAlert[]>(`subscription/private/v3/subscriptions/failed_renewal`, { params });
  }

  getFailedRenewalOrg(orgUuid: string, startDate: string, endDate: string) {
    const params = new HttpParams().append('startDate', startDate).append('endDate', endDate);
    return this.http.get<SubAlertEntry[]>(
      `subscription/private/v3/subscriptions/failed_renewal/organizations/${orgUuid}`,
      { params }
    );
  }

  getExpiring(domain: string, startDate: string, endDate: string) {
    const params = new HttpParams().append('domain', domain).append('startDate', startDate).append('endDate', endDate);
    return this.http.get<SubAlert[]>(`subscription/private/v3/subscriptions/expiring`, { params });
  }

  getExpiringOrg(orgUuid: string, startDate: string, endDate: string) {
    const params = new HttpParams().append('startDate', startDate).append('endDate', endDate);
    return this.http.get<SubAlertEntry[]>(`subscription/private/v3/subscriptions/expiring/organizations/${orgUuid}`, {
      params
    });
  }

  getFailedRenewalSubscriptions(
    orgUuid?: string,
    cursor?: string
  ): Observable<SubscriptionNotiResponse<SubscriptionNoti>> {
    let params = new HttpParams();
    if (orgUuid) {
      params = params.set('orgUuid', orgUuid);
    }
    if (cursor) {
      params = params.set('cursor', cursor);
    }

    return this.http.get<SubscriptionNotiResponse<SubscriptionNoti>>(
      `subscription/private/v4/subscriptions/failed_renewal`,
      { params }
    );
  }

  getExpiringSubscriptions(orgUuid?: string, cursor?: string): Observable<SubscriptionNotiResponse<SubscriptionNoti>> {
    let params = new HttpParams();
    if (orgUuid) {
      params = params.set('orgUuid', orgUuid);
    }
    if (cursor) {
      params = params.set('cursor', cursor);
    }

    return this.http.get<SubscriptionNotiResponse<SubscriptionNoti>>(`subscription/private/v4/subscriptions/expiring`, {
      params
    });
  }

  getAlignPrice(orgUuid: string, req: GetAlignPriceReq) {
    let headers = new HttpHeaders();
    if (orgUuid) {
      headers = headers.set(X_B3_HEADER.orgUuid, orgUuid);
    }
    return this.http.post<SubscriptionAmount[]>(`subscription/private/v3/subscriptions/align`, req, { headers });
  }
}
